import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Plus,
  RefreshCw,
  Edit2,
  Trash2,
  ArrowLeft,
  Save,
  X,
  Loader2,
  Scissors,
  Sparkles,
  Palette,
  Eye,
  Utensils,
  Shirt,
  Leaf,
  Plane,
  Heart,
  Cake,
  Camera,
  Calendar,
  Music,
  PartyPopper,
  Star,
  GripVertical,
  Check,
  AlertCircle,
  Users,
  Building2,
  TrendingUp,
  Shield,
  Crown,
  Settings,
  LayoutDashboard,
  FileText,
  Briefcase,
  Globe,
  Lock,
  BarChart3,
  UserCheck,
  Database,
  Activity,
  Zap,
  ChefHat,
  ShoppingBag,
  GraduationCap
} from "lucide-react";
import PilotConfigEditor from "@/components/admin/PilotConfigEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon options for categories and presets
const ICON_OPTIONS = [
  { value: "scissors", label: "Scissors", icon: Scissors },
  { value: "sparkles", label: "Sparkles", icon: Sparkles },
  { value: "palette", label: "Palette", icon: Palette },
  { value: "eye", label: "Eye", icon: Eye },
  { value: "utensils", label: "Utensils", icon: Utensils },
  { value: "shirt", label: "Shirt", icon: Shirt },
  { value: "leaf", label: "Leaf", icon: Leaf },
  { value: "plane", label: "Plane", icon: Plane },
  { value: "heart", label: "Heart", icon: Heart },
  { value: "cake", label: "Cake", icon: Cake },
  { value: "camera", label: "Camera", icon: Camera },
  { value: "calendar", label: "Calendar", icon: Calendar },
  { value: "music", label: "Music", icon: Music },
  { value: "party-popper", label: "Party", icon: PartyPopper },
  { value: "star", label: "Star", icon: Star },
  { value: "crown", label: "Crown", icon: Crown },
  { value: "briefcase", label: "Briefcase", icon: Briefcase },
  { value: "trending-up", label: "Trending", icon: TrendingUp },
];

// Color options
const COLOR_OPTIONS = [
  { value: "#8B5CF6", label: "Purple" },
  { value: "#EC4899", label: "Pink" },
  { value: "#EF4444", label: "Red" },
  { value: "#F59E0B", label: "Amber" },
  { value: "#10B981", label: "Emerald" },
  { value: "#22C55E", label: "Green" },
  { value: "#3B82F6", label: "Blue" },
  { value: "#6366F1", label: "Indigo" },
  { value: "#14B8A6", label: "Teal" },
  { value: "#F97316", label: "Orange" },
  { value: "#A855F7", label: "Violet" },
  { value: "#6B7280", label: "Gray" },
];

// Get icon component
const getIconComponent = (iconName) => {
  const found = ICON_OPTIONS.find(i => i.value === iconName);
  return found?.icon || Sparkles;
};

/**
 * ComprehensiveAdminPanel - Central admin panel for all platform configuration
 * Now embedded within Product page (not separate nav item)
 * Sections:
 * 1. Experience Categories (8 services)
 * 2. Event Presets (templates)
 * 3. Dashboard Templates
 * 4. Labels & Tags
 */
export default function ComprehensiveAdminPanel({ embedded = false }) {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("categories");
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [categories, setCategories] = useState([]);
  const [presets, setPresets] = useState([]);
  const [portals, setPortals] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  
  // Modal states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [currentItemType, setCurrentItemType] = useState("category"); // category, preset, portal, dashboard
  const [configuringPilot, setConfiguringPilot] = useState(null); // pilot_id being configured
  
  // Form data
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    description: "",
    icon: "sparkles",
    color: "#8B5CF6",
    visible: true,
    settings: {}
  });

  // Fetch all data on mount
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch categories
      const catRes = await fetch(`${API}/admin/experience-categories`, { headers });
      if (catRes.ok) {
        const catData = await catRes.json();
        setCategories(catData.categories || []);
      }
      
      // Fetch presets
      const presetRes = await fetch(`${API}/admin/event-presets`, { headers });
      if (presetRes.ok) {
        const presetData = await presetRes.json();
        setPresets(presetData.presets || []);
      }
      
      // Fetch portal sub-categories (includes investor, team, etc.)
      const portalRes = await fetch(`${API}/admin/portal-categories`, { headers });
      if (portalRes.ok) {
        const portalData = await portalRes.json();
        // Flatten all portal sub-categories
        const allPortals = (portalData.categories || []).flatMap(cat => 
          (cat.sub_categories || []).map(sub => ({ ...sub, parent_type: cat.category_type }))
        );
        setPortals(allPortals);
      }
      
      // Fetch organizations
      const orgRes = await fetch(`${API}/organizations/search?q=`, { headers });
      if (orgRes.ok) {
        const orgData = await orgRes.json();
        setOrganizations(orgData.organizations || []);
      }
      
      // Fetch admin stats
      const statsRes = await fetch(`${API}/admin/stats`, { headers });
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
      
      // Fetch recent users for sync verification
      const usersRes = await fetch(`${API}/admin/users?limit=20`, { headers });
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(usersData.users || []);
      }
      
    } catch (err) {
      console.error("Failed to fetch admin data:", err);
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  // Save category
  const saveCategory = async () => {
    if (!formData.name.trim()) {
      toast.error("Name is required");
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const isEdit = !!editingItem;
      
      const url = isEdit 
        ? `${API}/admin/experience-categories/${editingItem.category_id}`
        : `${API}/admin/experience-categories`;
      
      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ...formData,
          category_id: formData.id || formData.name.toLowerCase().replace(/\s+/g, '_')
        })
      });
      
      if (res.ok) {
        toast.success(isEdit ? "Category updated" : "Category created");
        setShowAddDialog(false);
        setEditingItem(null);
        fetchAllData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to save");
      }
    } catch (err) {
      toast.error("Failed to save category");
    } finally {
      setSaving(false);
    }
  };

  // Save preset
  const savePreset = async () => {
    if (!formData.name.trim()) {
      toast.error("Name is required");
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const isEdit = !!editingItem;
      
      const url = isEdit 
        ? `${API}/admin/event-presets/${editingItem.preset_id}`
        : `${API}/admin/event-presets`;
      
      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ...formData,
          preset_id: formData.id || formData.name.toLowerCase().replace(/\s+/g, '_')
        })
      });
      
      if (res.ok) {
        toast.success(isEdit ? "Preset updated" : "Preset created");
        setShowAddDialog(false);
        setEditingItem(null);
        fetchAllData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to save");
      }
    } catch (err) {
      toast.error("Failed to save preset");
    } finally {
      setSaving(false);
    }
  };

  // Delete category
  const deleteCategory = async (categoryId) => {
    if (!confirm("Are you sure you want to delete this category?")) return;
    
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/experience-categories/${categoryId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success("Category deleted");
        fetchAllData();
      } else {
        toast.error("Failed to delete category");
      }
    } catch (err) {
      toast.error("Failed to delete category");
    }
  };

  // Delete preset
  const deletePreset = async (presetId) => {
    if (presetId === "custom") {
      toast.error("Cannot delete the 'custom' preset");
      return;
    }
    if (!confirm("Are you sure you want to delete this preset?")) return;
    
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/event-presets/${presetId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success("Preset deleted");
        fetchAllData();
      } else {
        toast.error("Failed to delete preset");
      }
    } catch (err) {
      toast.error("Failed to delete preset");
    }
  };

  // Open add dialog
  const openAddDialog = (type) => {
    setFormData({
      id: "",
      name: "",
      description: "",
      icon: type === "preset" ? "calendar" : "sparkles",
      color: "#8B5CF6",
      visible: true,
      settings: {}
    });
    setEditingItem(null);
    setCurrentItemType(type);
    setShowAddDialog(true);
  };

  // Open edit dialog
  const openEditDialog = (item, type) => {
    setFormData({
      id: item.category_id || item.preset_id || item.sub_category_id || item.id,
      name: item.name,
      description: item.description || "",
      icon: item.icon || "sparkles",
      color: item.color || "#8B5CF6",
      visible: item.visible !== false && item.visible_to_all !== false,
      settings: item.settings || {}
    });
    setEditingItem(item);
    setCurrentItemType(type);
    setShowAddDialog(true);
  };

  // Handle save based on type
  const handleSave = () => {
    switch (currentItemType) {
      case "category":
        saveCategory();
        break;
      case "preset":
        savePreset();
        break;
      default:
        saveCategory();
    }
  };

  // Verify user data sync
  const verifyUserSync = async (userId) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/me/all-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success("User data is synced correctly");
      }
    } catch (err) {
      toast.error("Sync verification failed");
    }
  };

  // Render section navigation
  // NOTE: Data Sync and Analytics removed - that data is shown per-user in People database
  // and Analytics already exists as its own sidebar item
  const SectionNav = () => (
    <div className="flex flex-wrap gap-2 mb-6">
      {[
        { id: "categories", label: "Experience Categories", icon: Sparkles, count: categories.length },
        { id: "presets", label: "Experience Presets", icon: Calendar, count: presets.length },
        { id: "quickactions", label: "Quick Actions", icon: Zap, count: null },
        { id: "portals", label: "Portals", icon: Globe, count: portals.length },
        { id: "organizations", label: "Hosts", icon: Building2, count: organizations.length },
        { id: "metemplate", label: "/me Template", icon: FileText, count: null },
      ].map(section => (
        <Button
          key={section.id}
          variant={activeSection === section.id ? "default" : "outline"}
          onClick={() => setActiveSection(section.id)}
          className="flex items-center gap-2"
          data-testid={`section-${section.id}`}
        >
          <section.icon className="w-4 h-4" />
          {section.label}
          {section.count !== null && (
            <Badge variant="secondary" className="ml-1">{section.count}</Badge>
          )}
        </Button>
      ))}
    </div>
  );

  // Render Experience Categories Section
  const CategoriesSection = () => (
    <div className="space-y-4" data-testid="categories-section">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Experience Categories</h3>
          <p className="text-sm text-gray-500">8 service types users can plan (Hair, Nail, Makeup, etc.)</p>
        </div>
        <Button onClick={() => openAddDialog("category")} data-testid="add-category-btn">
          <Plus className="w-4 h-4 mr-1" />
          Add Category
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {categories.map((cat) => {
          const IconComponent = getIconComponent(cat.icon);
          return (
            <Card key={cat.category_id} className="overflow-hidden" data-testid={`category-${cat.category_id}`}>
              <div className="h-2" style={{ backgroundColor: cat.color }} />
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${cat.color}20` }}
                    >
                      <IconComponent className="w-5 h-5" style={{ color: cat.color }} />
                    </div>
                    <div>
                      <h4 className="font-medium">{cat.name}</h4>
                      <p className="text-xs text-gray-500 truncate max-w-[120px]">{cat.description}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditDialog(cat, "category")}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => deleteCategory(cat.category_id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <Badge variant="outline" className="text-xs">{cat.category_id}</Badge>
                  <Badge className={cat.visible_to_all !== false ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
                    {cat.visible_to_all !== false ? "Visible" : "Hidden"}
                  </Badge>
                </div>
                {/* Configure Defaults, Inspo, Intake */}
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full mt-3 gap-2"
                  onClick={() => setConfiguringPilot(cat.category_id)}
                >
                  <Settings className="w-4 h-4" />
                  Configure Defaults & Intake
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Pilot Configuration Dialog */}
      <Dialog open={!!configuringPilot} onOpenChange={(open) => !open && setConfiguringPilot(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="capitalize">{configuringPilot} Pilot Configuration</DialogTitle>
            <DialogDescription>
              Configure requested defaults, inspo folders, and intake questions
            </DialogDescription>
          </DialogHeader>
          {configuringPilot && (
            <PilotConfigEditor 
              pilotId={configuringPilot} 
              onSave={() => {
                setConfiguringPilot(null);
                toast.success("Configuration saved!");
              }}
              onClose={() => setConfiguringPilot(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );

  // Render Event Presets Section
  const PresetsSection = () => (
    <div className="space-y-4" data-testid="presets-section">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Event Presets</h3>
          <p className="text-sm text-gray-500">Templates for planning (Wedding, Birthday, Photoshoot, etc.)</p>
        </div>
        <Button onClick={() => openAddDialog("preset")} data-testid="add-preset-btn">
          <Plus className="w-4 h-4 mr-1" />
          Add Preset
        </Button>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-800">
          <strong>Note:</strong> Event presets are templates for planning. Actual RSVPd events (like HTHF) sync from the events system and are NOT presets.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {presets.map((preset) => {
          const IconComponent = getIconComponent(preset.icon);
          return (
            <Card key={preset.preset_id} className="overflow-hidden" data-testid={`preset-${preset.preset_id}`}>
              <div className="h-2" style={{ backgroundColor: preset.color }} />
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${preset.color}20` }}
                    >
                      <IconComponent className="w-5 h-5" style={{ color: preset.color }} />
                    </div>
                    <div>
                      <h4 className="font-medium">{preset.name}</h4>
                      <p className="text-xs text-gray-500">{preset.description}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditDialog(preset, "preset")}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    {preset.preset_id !== "custom" && (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => deletePreset(preset.preset_id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
                <Badge variant="outline" className="text-xs mt-3">{preset.preset_id}</Badge>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );

  // Render Quick Actions Section
  const QuickActionsSection = () => {
    const [quickActions, setQuickActions] = useState([
      // Event Dashboard Quick Actions
      { id: "share_with_chef", title: "Share with Chef", subtitle: "Dietary summary", icon: "utensils", color: "orange", dashboard_type: "event", enabled: true },
      { id: "share_with_venue", title: "Share with Venue", subtitle: "Accessibility needs", icon: "accessibility", color: "blue", dashboard_type: "event", enabled: true },
      { id: "share_with_dj", title: "Share with DJ", subtitle: "Tap In playlist", icon: "music", color: "purple", dashboard_type: "event", enabled: true },
      // Instructor Dashboard Quick Actions
      { id: "share_with_instructor", title: "Share with Instructor", subtitle: "Attendee summary", icon: "graduation-cap", color: "indigo", dashboard_type: "instructor", enabled: true },
      { id: "share_accessibility_instructor", title: "Share Accessibility", subtitle: "Accessibility needs", icon: "accessibility", color: "blue", dashboard_type: "instructor", enabled: true },
      { id: "share_music_instructor", title: "Share Music Preferences", subtitle: "Attendee music picks", icon: "music", color: "purple", dashboard_type: "instructor", enabled: true },
    ]);
    const [editingAction, setEditingAction] = useState(null);
    const [showEditDialog, setShowEditDialog] = useState(false);
    
    const handleEditAction = (action) => {
      setEditingAction({ ...action });
      setShowEditDialog(true);
    };
    
    const handleSaveAction = async () => {
      if (!editingAction) return;
      
      setQuickActions(prev => 
        prev.map(a => a.id === editingAction.id ? editingAction : a)
      );
      
      // TODO: Save to backend
      toast.success("Quick action updated!");
      setShowEditDialog(false);
      setEditingAction(null);
    };
    
    return (
      <div className="space-y-6" data-testid="quickactions-section">
        <div>
          <h3 className="text-lg font-semibold">Quick Actions Configuration</h3>
          <p className="text-sm text-gray-500">Configure quick action buttons for Event and Instructor dashboards. Provider dashboards (Makeup, Culinary, etc.) do not have quick actions.</p>
        </div>
        
        {/* Event Dashboard Quick Actions */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <PartyPopper className="w-4 h-4 text-purple-500" />
              Event Dashboard Quick Actions
            </CardTitle>
            <CardDescription>These actions appear on event host dashboards</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {quickActions.filter(a => a.dashboard_type === "event").map(action => (
              <div 
                key={action.id}
                className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg bg-${action.color}-100 flex items-center justify-center`}>
                    <Zap className={`w-5 h-5 text-${action.color}-600`} />
                  </div>
                  <div>
                    <p className="font-medium">{action.title}</p>
                    <p className="text-sm text-gray-500">{action.subtitle}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={action.enabled} />
                  <Button variant="ghost" size="sm" onClick={() => handleEditAction(action)}>
                    <Edit2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
        
        {/* Instructor Dashboard Quick Actions */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <GripVertical className="w-4 h-4 text-blue-500" />
              Instructor Dashboard Quick Actions
            </CardTitle>
            <CardDescription>These actions appear on instructor/workshop host dashboards</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {quickActions.filter(a => a.dashboard_type === "instructor").map(action => (
              <div 
                key={action.id}
                className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg bg-${action.color}-100 flex items-center justify-center`}>
                    <Zap className={`w-5 h-5 text-${action.color}-600`} />
                  </div>
                  <div>
                    <p className="font-medium">{action.title}</p>
                    <p className="text-sm text-gray-500">{action.subtitle}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={action.enabled} />
                  <Button variant="ghost" size="sm" onClick={() => handleEditAction(action)}>
                    <Edit2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
        
        {/* Provider Notice */}
        <Card className="bg-gray-50 border-dashed">
          <CardContent className="py-6 text-center">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            <p className="font-medium text-gray-600">Provider Dashboards</p>
            <p className="text-sm text-gray-500 mt-1">
              Makeup, Culinary, Hair, Nails, and other provider dashboards do not have quick actions.
            </p>
          </CardContent>
        </Card>
        
        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Quick Action</DialogTitle>
              <DialogDescription>Customize the title and subtitle for this quick action</DialogDescription>
            </DialogHeader>
            {editingAction && (
              <div className="space-y-4 py-4">
                <div>
                  <Label>Title</Label>
                  <Input 
                    value={editingAction.title}
                    onChange={(e) => setEditingAction(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Share with Chef"
                  />
                </div>
                <div>
                  <Label>Subtitle</Label>
                  <Input 
                    value={editingAction.subtitle}
                    onChange={(e) => setEditingAction(prev => ({ ...prev, subtitle: e.target.value }))}
                    placeholder="e.g., Dietary summary"
                  />
                </div>
                <div>
                  <Label>Color</Label>
                  <Select 
                    value={editingAction.color}
                    onValueChange={(v) => setEditingAction(prev => ({ ...prev, color: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="orange">Orange</SelectItem>
                      <SelectItem value="blue">Blue</SelectItem>
                      <SelectItem value="purple">Purple</SelectItem>
                      <SelectItem value="green">Green</SelectItem>
                      <SelectItem value="pink">Pink</SelectItem>
                      <SelectItem value="indigo">Indigo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveAction}>Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  // Render Portals Section
  const PortalsSection = () => (
    <div className="space-y-4" data-testid="portals-section">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Portals</h3>
          <p className="text-sm text-gray-500">Investor, Team, Network, and other portal configurations</p>
        </div>
        <Button onClick={() => navigate("/admin/pilots")} variant="outline">
          <Settings className="w-4 h-4 mr-1" />
          Manage Pilots
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {portals.map((portal) => {
          const IconComponent = getIconComponent(portal.icon);
          return (
            <Card key={portal.sub_category_id} className="overflow-hidden" data-testid={`portal-${portal.sub_category_id}`}>
              <div className="h-2" style={{ backgroundColor: portal.color || "#8B5CF6" }} />
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${portal.color || "#8B5CF6"}20` }}
                    >
                      <IconComponent className="w-5 h-5" style={{ color: portal.color || "#8B5CF6" }} />
                    </div>
                    <div>
                      <h4 className="font-medium">{portal.name}</h4>
                      <p className="text-xs text-gray-500 truncate max-w-[150px]">{portal.description}</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <Badge variant="outline" className="text-xs">{portal.parent_type || "portal"}</Badge>
                  <Badge className="bg-blue-100 text-blue-700 text-xs">
                    {portal.members?.length || 0} members
                  </Badge>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );

  // Render Organizations Section
  const OrganizationsSection = () => (
    <div className="space-y-4" data-testid="organizations-section">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Hosts</h3>
          <p className="text-sm text-gray-500">All registered hosts on the platform</p>
        </div>
        <Button onClick={fetchAllData} variant="outline">
          <RefreshCw className="w-4 h-4 mr-1" />
          Refresh
        </Button>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Members</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {organizations.slice(0, 20).map((org) => (
              <TableRow key={org.org_id} data-testid={`org-row-${org.org_id}`}>
                <TableCell className="font-medium">{org.name}</TableCell>
                <TableCell>
                  <Badge variant="outline">{org.type || "general"}</Badge>
                </TableCell>
                <TableCell>{org.member_count || org.members?.length || 0}</TableCell>
                <TableCell>
                  <Badge className={org.is_active !== false ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
                    {org.is_active !== false ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-gray-500">
                  {org.created_at ? new Date(org.created_at).toLocaleDateString() : "N/A"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );

  // Render Data Sync Section
  const DataSyncSection = () => (
    <div className="space-y-4" data-testid="sync-section">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Data Sync Verification</h3>
          <p className="text-sm text-gray-500">Verify user data flows correctly: Intake → Admin → /me/settings</p>
        </div>
        <Button onClick={fetchAllData} variant="outline">
          <RefreshCw className="w-4 h-4 mr-1" />
          Refresh Users
        </Button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
          <Database className="w-5 h-5" />
          Organization Sync Flow
        </h4>
        <ol className="list-decimal list-inside text-sm text-blue-800 space-y-1">
          <li>User submits intake form with organization info</li>
          <li>Organization appears in Admin Panel (pending approval)</li>
          <li>Admin approves organization</li>
          <li>Organization appears in user's /me/settings page</li>
        </ol>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent Users (Sync Status)</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Hosts</TableHead>
                <TableHead>Last Sync</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.slice(0, 10).map((user) => (
                <TableRow key={user.user_id} data-testid={`user-row-${user.user_id}`}>
                  <TableCell className="font-medium">
                    {user.first_name || user.name || "N/A"} {user.last_name || ""}
                  </TableCell>
                  <TableCell className="text-sm">{user.email || "N/A"}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(user.organizations || []).slice(0, 2).map((org, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {typeof org === 'string' ? org : org.name || org.org_id}
                        </Badge>
                      ))}
                      {(user.organizations || []).length > 2 && (
                        <Badge variant="outline" className="text-xs">+{user.organizations.length - 2}</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-gray-500">
                    {user.updated_at ? new Date(user.updated_at).toLocaleString() : "N/A"}
                  </TableCell>
                  <TableCell>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => verifyUserSync(user.user_id)}
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Verify
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );

  // Render Analytics Section
  const AnalyticsSection = () => (
    <div className="space-y-4" data-testid="analytics-section">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Platform Analytics</h3>
          <p className="text-sm text-gray-500">Overview of platform metrics and activity</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-green-100 text-green-700">
            <Activity className="w-3 h-3 mr-1" />
            Live
          </Badge>
          <Button onClick={fetchAllData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <p className="text-2xl font-bold">{stats.total_users?.toLocaleString() || 0}</p>
            <p className="text-sm text-gray-500">Total Users</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Calendar className="w-5 h-5 text-pink-600" />
            </div>
            <p className="text-2xl font-bold">{stats.total_events?.toLocaleString() || 0}</p>
            <p className="text-sm text-gray-500">Total Events</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Building2 className="w-5 h-5 text-amber-600" />
            </div>
            <p className="text-2xl font-bold">{stats.total_organizations?.toLocaleString() || 0}</p>
            <p className="text-sm text-gray-500">Hosts</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Zap className="w-5 h-5 text-blue-600" />
            </div>
            <p className="text-2xl font-bold">{stats.total_rsvps?.toLocaleString() || 0}</p>
            <p className="text-sm text-gray-500">Total RSVPs</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
            WebSocket Analytics Connection
          </CardTitle>
          <CardDescription>Real-time data streaming status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-sm font-medium">Event Analytics WebSocket</span>
              </div>
              <Badge>ws/event-analytics</Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-sm font-medium">Admin Activity WebSocket</span>
              </div>
              <Badge>ws/admin-activity</Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-sm font-medium">VIP List WebSocket</span>
              </div>
              <Badge>ws/vip-list</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  // Render /me Template & Inspo Defaults Section
  const MeTemplateSection = () => {
    const [meTemplateState, setMeTemplateState] = useState({});
    const [inspoDefaults, setInspoDefaults] = useState({});
    const [templateLoading, setTemplateLoading] = useState(true);
    const [savingTemplate, setSavingTemplate] = useState(false);
    const [newDefaultFolder, setNewDefaultFolder] = useState("");
    const [selectedDefaultCat, setSelectedDefaultCat] = useState("");
    
    const templateFields = [
      { key: "hosting_cta_title", label: "Hosting CTA Title", placeholder: "Host Your Own Experience" },
      { key: "hosting_cta_description", label: "Hosting CTA Description", placeholder: "Create and manage your events..." },
      { key: "hosting_cta_button", label: "Hosting CTA Button", placeholder: "View My Events" },
      { key: "dashboard_title", label: "Dashboard Tab", placeholder: "Dashboard" },
      { key: "defaults_title", label: "Defaults Tab", placeholder: "Defaults" },
      { key: "inspo_title", label: "Inspo Tab", placeholder: "Inspiration" },
      { key: "experience_title", label: "Experience Tab", placeholder: "Experiences" },
      { key: "settings_title", label: "Settings Tab", placeholder: "Settings" },
      { key: "upcoming_title", label: "Upcoming Section", placeholder: "Upcoming" },
      { key: "hosting_title", label: "Hosting Section", placeholder: "Hosting" },
      { key: "preferences_title", label: "Preferences Section", placeholder: "Preference Card" },
    ];
    
    useEffect(() => {
      const token = localStorage.getItem("session_token");
      if (!token) return;
      Promise.all([
        fetch(`${API}/admin/me-template`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : { sections: {} }),
        fetch(`${API}/admin/inspo-subfolder-defaults`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : { folders: {} }),
      ]).then(([tpl, inspo]) => {
        setMeTemplateState(tpl.sections || {});
        setInspoDefaults(inspo.folders || {});
        setTemplateLoading(false);
      }).catch(() => setTemplateLoading(false));
    }, []);
    
    const saveTemplate = async () => {
      setSavingTemplate(true);
      const token = localStorage.getItem("session_token");
      try {
        await fetch(`${API}/admin/me-template`, {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ sections: meTemplateState })
        });
        toast.success("Template saved!");
      } catch { toast.error("Failed to save"); }
      setSavingTemplate(false);
    };
    
    const saveInspoDefaults = async () => {
      const token = localStorage.getItem("session_token");
      try {
        await fetch(`${API}/admin/inspo-subfolder-defaults`, {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ folders: inspoDefaults })
        });
        toast.success("Inspo preferences saved!");
      } catch { toast.error("Failed to save"); }
    };
    
    const addDefaultFolder = (cat) => {
      if (!newDefaultFolder.trim() || !cat) return;
      const updated = { ...inspoDefaults };
      if (!updated[cat]) updated[cat] = [];
      updated[cat] = [...updated[cat], newDefaultFolder.trim()];
      setInspoDefaults(updated);
      setNewDefaultFolder("");
    };
    
    const removeDefaultFolder = (cat, folder) => {
      const updated = { ...inspoDefaults };
      updated[cat] = (updated[cat] || []).filter(f => f !== folder);
      setInspoDefaults(updated);
    };
    
    const clearCategoryDefaults = (cat) => {
      const updated = { ...inspoDefaults };
      updated[cat] = [];
      setInspoDefaults(updated);
    };
    
    if (templateLoading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>;
    
    return (
      <div className="space-y-6" data-testid="metemplate-section">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-4 h-4 text-purple-500" />
                  /me Page Template
                </CardTitle>
                <CardDescription>Edit text that appears on the personal dashboard. Changes sync live to all /me pages.</CardDescription>
              </div>
              <Button onClick={saveTemplate} disabled={savingTemplate} size="sm" data-testid="save-me-template-btn">
                {savingTemplate ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Save Template
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {templateFields.map(field => (
                <div key={field.key}>
                  <Label className="text-xs text-gray-500">{field.label}</Label>
                  <Input
                    value={meTemplateState[field.key] || ""}
                    onChange={(e) => setMeTemplateState(prev => ({ ...prev, [field.key]: e.target.value }))}
                    placeholder={field.placeholder}
                    className="mt-1"
                    data-testid={`template-field-${field.key}`}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <Camera className="w-4 h-4 text-pink-500" />
                  Inspo Subfolder Defaults
                </CardTitle>
                <CardDescription>Default subfolder suggestions per category. Users can customize their own.</CardDescription>
              </div>
              <Button onClick={saveInspoDefaults} size="sm" variant="outline" data-testid="save-inspo-defaults-btn">
                <Save className="w-4 h-4 mr-1" /> Save Defaults
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {categories.map(cat => {
                const catFolders = inspoDefaults[cat.sub_category_id] || [];
                return (
                  <div key={cat.sub_category_id} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                        <span className="text-sm font-medium">{cat.name}</span>
                        <Badge variant="outline" className="text-[10px]">{catFolders.length} folders</Badge>
                      </div>
                      <Button variant="ghost" size="sm" className="text-xs text-red-500 h-6" onClick={() => clearCategoryDefaults(cat.sub_category_id)}>
                        Clear
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {catFolders.map(folder => (
                        <Badge key={folder} variant="secondary" className="text-xs gap-1">
                          {folder}
                          <button onClick={() => removeDefaultFolder(cat.sub_category_id, folder)} className="ml-0.5 opacity-50 hover:opacity-100">
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-1">
                      <Input
                        placeholder="Add folder..."
                        className="h-7 text-xs flex-1"
                        value={selectedDefaultCat === cat.sub_category_id ? newDefaultFolder : ""}
                        onFocus={() => setSelectedDefaultCat(cat.sub_category_id)}
                        onChange={(e) => { setSelectedDefaultCat(cat.sub_category_id); setNewDefaultFolder(e.target.value); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { addDefaultFolder(cat.sub_category_id); } }}
                      />
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => addDefaultFolder(cat.sub_category_id)}>
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };


  return (
    <div className="min-h-screen bg-gray-50" data-testid="comprehensive-admin-panel">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin")}>
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <div>
              <h1 className="font-semibold text-lg">Host Dashboard Settings</h1>
              <p className="text-sm text-gray-500">Manage all platform configuration</p>
            </div>
          </div>
          <Button onClick={fetchAllData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh All
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <SectionNav />
        
        {activeSection === "categories" && <CategoriesSection />}
        {activeSection === "presets" && <PresetsSection />}
        {activeSection === "quickactions" && <QuickActionsSection />}
        {activeSection === "portals" && <PortalsSection />}
        {activeSection === "organizations" && <OrganizationsSection />}
        {activeSection === "metemplate" && <MeTemplateSection />}
      </main>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingItem ? `Edit ${currentItemType}` : `Add ${currentItemType}`}
            </DialogTitle>
            <DialogDescription>
              {currentItemType === "category" 
                ? "Service categories appear in the 'Plan an Experience' modal"
                : "Event presets appear in the 'Plan for an Event' modal"
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={currentItemType === "category" ? "e.g., Hair" : "e.g., Photoshoot"}
                data-testid="item-name-input"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description..."
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Icon</Label>
                <Select value={formData.icon} onValueChange={(v) => setFormData({ ...formData, icon: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ICON_OPTIONS.map((opt) => {
                      const Icon = opt.icon;
                      return (
                        <SelectItem key={opt.value} value={opt.value}>
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4" />
                            {opt.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Color</Label>
                <Select value={formData.color} onValueChange={(v) => setFormData({ ...formData, color: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COLOR_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: opt.value }} />
                          {opt.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label>Visible to users</Label>
              <Switch
                checked={formData.visible}
                onCheckedChange={(v) => setFormData({ ...formData, visible: v })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-item-btn">
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {editingItem ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
